/**
 * QSIS Emotion Analyzer Component
 * ================================
 * Real-time emotion and sentiment analysis during phone calls
 * Integrates with QSIS Voice Analysis API
 *
 * Features:
 * - Real-time voice emotion detection
 * - Sentiment tracking (positive/negative/neutral)
 * - Smart coaching tips based on call type (sales/service)
 * - Visual emotion indicators
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';

// QSIS API endpoint
const QSIS_API = 'https://qsis.ink';

// Emotion data with Hebrew labels
const EMOTION_DATA = {
  happy: { emoji: '😊', name: 'שמח', nameEn: 'Happy', color: '#4CAF50' },
  sad: { emoji: '😢', name: 'עצוב', nameEn: 'Sad', color: '#2196F3' },
  angry: { emoji: '😠', name: 'כועס', nameEn: 'Angry', color: '#f44336' },
  fear: { emoji: '😨', name: 'מפחד', nameEn: 'Afraid', color: '#9C27B0' },
  excited: { emoji: '🤩', name: 'נרגש', nameEn: 'Excited', color: '#FF9800' },
  neutral: { emoji: '😐', name: 'נייטרלי', nameEn: 'Neutral', color: '#607D8B' }
};

// Coaching tips based on call type and emotion
const COACHING_TIPS = {
  service: {
    angry: {
      type: 'urgent',
      icon: '🚨',
      title: 'הלקוח כועס!',
      text: 'עצור! כרגע פתרון לא יעזור. התמקד בהרגעה: "אני מבין את התסכול שלך"'
    },
    sad: {
      type: 'warning',
      icon: '💙',
      title: 'הלקוח עצוב/מתוסכל',
      text: 'הראה אמפתיה לפני שמציע פתרון. תן ללקוח להרגיש ששומעים אותו.'
    },
    happy: {
      type: 'success',
      icon: '🎉',
      title: 'כל הכבוד!',
      text: 'הלקוח שמח! עבודה יפה. זה הזמן לסיים בחיוב.'
    },
    neutral: {
      type: 'info',
      icon: '👂',
      title: 'הלקוח נייטרלי',
      text: 'המשך להקשיב ולהבין את הבעיה לעומק.'
    },
    excited: {
      type: 'success',
      icon: '⭐',
      title: 'הלקוח נלהב!',
      text: 'מצב מצוין! נצל את ההזדמנות לחזק את הקשר.'
    },
    fear: {
      type: 'warning',
      icon: '🤝',
      title: 'הלקוח מודאג',
      text: 'הרגע את הלקוח והבטח לו שאתה כאן לעזור.'
    }
  },
  sales: {
    happy: {
      type: 'success',
      icon: '🎯',
      title: 'הלקוח מעוניין!',
      text: 'זה הזמן להניע לפעולה! שאל: "מתי נוח לך להתחיל?"'
    },
    excited: {
      type: 'success',
      icon: '🔥',
      title: 'הלקוח נלהב!',
      text: 'רגע מושלם לסגירה! הצע הטבה מיוחדת.'
    },
    neutral: {
      type: 'info',
      icon: '🤔',
      title: 'הלקוח מהסס',
      text: 'שאל שאלות פתוחות: "מה עוד היית רוצה לדעת?"'
    },
    sad: {
      type: 'warning',
      icon: '🎣',
      title: 'הלקוח לא באמת מעוניין',
      text: 'נסה לזהות את הצורך האמיתי. שאל: "מה היית רוצה שזה יפתור לך?"'
    },
    angry: {
      type: 'urgent',
      icon: '🛡️',
      title: 'התנגדות!',
      text: 'הקשב להתנגדות ואל תתגונן. שאל: "מה גורם לך לחשוב כך?"'
    },
    fear: {
      type: 'warning',
      icon: '💡',
      title: 'הלקוח חושש',
      text: 'טפל בחששות לפני שממשיך. מה מדאיג אותו?'
    }
  }
};

// Sentiment word lists (Hebrew + English)
const POSITIVE_WORDS = [
  'טוב', 'מעולה', 'אהבה', 'שמח', 'נהדר', 'יפה', 'מדהים', 'נפלא', 'אוהב', 'אוהבת',
  'מסכים', 'מסכימה', 'בסדר', 'תודה', 'כן', 'בטח', 'נכון', 'מושלם', 'מצוין', 'סבבה',
  'אחלה', 'יופי', 'מגניב', 'משמח', 'מרוצה', 'מעוניין', 'רוצה', 'בבקשה',
  'good', 'great', 'happy', 'love', 'amazing', 'wonderful', 'excellent', 'perfect', 'yes', 'thanks'
];

const NEGATIVE_WORDS = [
  'רע', 'נורא', 'עצוב', 'כועס', 'מתוסכל', 'לא', 'אין', 'בעיה', 'גרוע', 'איום',
  'שונא', 'שונאת', 'מפחד', 'מפחדת', 'דואג', 'דואגת', 'עצבני', 'עצבנית', 'מאוכזב',
  'מאוכזבת', 'נגד', 'מסרב', 'מסרבת', 'אסור', 'קשה', 'סובל', 'כואב', 'נמאס', 'מעצבן',
  'bad', 'terrible', 'hate', 'awful', 'horrible', 'sad', 'angry', 'frustrated', 'no', 'problem'
];

/**
 * Analyze sentiment from text
 */
const analyzeSentiment = (text) => {
  if (!text) return 0;

  const words = text.toLowerCase().split(/\s+/);
  let score = 0;

  words.forEach(word => {
    if (POSITIVE_WORDS.some(p => word.includes(p))) score += 0.2;
    if (NEGATIVE_WORDS.some(n => word.includes(n))) score -= 0.2;
  });

  return Math.max(-1, Math.min(1, score));
};

/**
 * Detect emotion from voice metrics
 */
const detectEmotion = (pitch, energy, sentiment) => {
  let emotions = {
    happy: 0.1,
    sad: 0.1,
    angry: 0.1,
    fear: 0.1,
    excited: 0.1,
    neutral: 0.5
  };

  // Voice analysis
  if (pitch > 200) emotions.excited += 0.3;
  if (pitch < 120) emotions.sad += 0.3;
  if (energy > 0.6) emotions.angry += 0.3;
  if (energy < 0.2) emotions.sad += 0.2;

  // Sentiment influence
  if (sentiment > 0.3) {
    emotions.happy += 0.4;
    emotions.excited += 0.2;
  } else if (sentiment < -0.3) {
    emotions.angry += 0.3;
    emotions.sad += 0.2;
  }

  // Normalize
  const total = Object.values(emotions).reduce((a, b) => a + b);
  Object.keys(emotions).forEach(key => emotions[key] /= total);

  // Find dominant
  let dominant = 'neutral';
  let maxVal = 0;
  Object.entries(emotions).forEach(([emotion, value]) => {
    if (value > maxVal) {
      maxVal = value;
      dominant = emotion;
    }
  });

  return { dominant, confidence: maxVal, all: emotions };
};

/**
 * Main QSIS Emotion Analyzer Component
 */
const QSISEmotionAnalyzer = ({
  isActive = false,
  callType = 'service', // 'service' or 'sales'
  onEmotionChange = () => {},
  onTipGenerated = () => {},
  onStatsChange = () => {}, // Callback for sentiment stats
  onTranscriptChange = () => {}, // Callback for transcript text
  emotionDataRef = null, // External ref to track emotion data
  compact = false
}) => {
  const [emotion, setEmotion] = useState({ dominant: 'neutral', confidence: 0.5 });
  const [sentiment, setSentiment] = useState(0);
  const [pitch, setPitch] = useState(0);
  const [energy, setEnergy] = useState(0);
  const [currentTip, setCurrentTip] = useState(null);
  const [stats, setStats] = useState({ positive: 0, negative: 0, neutral: 0 });
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [allTranscripts, setAllTranscripts] = useState([]);
  const isFirstEmotionRef = useRef(true);

  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const recognitionRef = useRef(null);
  const animationRef = useRef(null);

  // Start voice analysis
  const startAnalysis = useCallback(async () => {
    try {
      // Get microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      // Setup audio analysis
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 2048;

      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);

      // Setup speech recognition
      if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        recognitionRef.current = new SpeechRecognition();
        recognitionRef.current.continuous = true;
        recognitionRef.current.interimResults = true;
        recognitionRef.current.lang = 'he-IL';

        recognitionRef.current.onresult = (event) => {
          const result = event.results[event.results.length - 1];
          if (result.isFinal) {
            const text = result[0].transcript;
            setTranscript(text);

            // Add to all transcripts
            const segment = {
              time: new Date().toISOString(),
              text: text,
              speaker: 'unknown' // Could be enhanced with speaker detection
            };
            setAllTranscripts(prev => [...prev, segment]);

            // Update external ref
            if (emotionDataRef) {
              emotionDataRef.current.transcriptSegments.push(segment);
              emotionDataRef.current.fullTranscript += (emotionDataRef.current.fullTranscript ? ' ' : '') + text;
            }

            // Analyze sentiment
            const sentimentScore = analyzeSentiment(text);
            setSentiment(sentimentScore);

            // Update stats
            setStats(prev => {
              const newStats = { ...prev };
              if (sentimentScore > 0.2) {
                newStats.positive = prev.positive + 1;
                if (emotionDataRef) emotionDataRef.current.positiveCount++;
              } else if (sentimentScore < -0.2) {
                newStats.negative = prev.negative + 1;
                if (emotionDataRef) emotionDataRef.current.negativeCount++;
              } else {
                newStats.neutral = prev.neutral + 1;
                if (emotionDataRef) emotionDataRef.current.neutralCount++;
              }
              return newStats;
            });
          }
        };

        recognitionRef.current.onerror = (event) => {
          if (event.error === 'no-speech' && isActive) {
            try { recognitionRef.current.start(); } catch (e) {}
          }
        };

        recognitionRef.current.onend = () => {
          if (isActive) {
            try { recognitionRef.current.start(); } catch (e) {}
          }
        };

        recognitionRef.current.start();
      }

      setIsListening(true);

      // Start analysis loop
      const analyze = () => {
        if (!analyserRef.current || !isActive) return;

        const bufferLength = analyserRef.current.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);
        analyserRef.current.getByteFrequencyData(dataArray);

        // Calculate energy
        const avgEnergy = dataArray.reduce((a, b) => a + b) / bufferLength / 255;
        setEnergy(avgEnergy);

        // Estimate pitch
        let maxIndex = 0;
        let maxValue = 0;
        for (let i = 0; i < bufferLength / 2; i++) {
          if (dataArray[i] > maxValue) {
            maxValue = dataArray[i];
            maxIndex = i;
          }
        }
        const estimatedPitch = 80 + (maxIndex / (bufferLength / 2)) * 200;
        setPitch(estimatedPitch);

        // Detect emotion
        const detectedEmotion = detectEmotion(estimatedPitch, avgEnergy, sentiment);
        setEmotion(detectedEmotion);
        onEmotionChange(detectedEmotion);

        // Track start emotion (first detection)
        if (isFirstEmotionRef.current && emotionDataRef) {
          emotionDataRef.current.startEmotion = detectedEmotion;
          isFirstEmotionRef.current = false;
        }

        // Always update end emotion
        if (emotionDataRef) {
          emotionDataRef.current.endEmotion = detectedEmotion;
        }

        // Generate coaching tip
        const tips = COACHING_TIPS[callType];
        const tip = tips[detectedEmotion.dominant];
        if (tip && (!currentTip || currentTip.title !== tip.title)) {
          setCurrentTip(tip);
          onTipGenerated(tip);

          // Track coaching tips
          if (emotionDataRef) {
            emotionDataRef.current.coachingTips.push({
              time: new Date().toISOString(),
              tip: tip.title,
              emotion: detectedEmotion.dominant
            });
          }
        }

        animationRef.current = requestAnimationFrame(analyze);
      };

      analyze();

    } catch (err) {
      console.error('[QSISAnalyzer] Error starting analysis:', err);
    }
  }, [isActive, callType, sentiment, onEmotionChange, onTipGenerated, currentTip, emotionDataRef]);

  // Stop voice analysis
  const stopAnalysis = useCallback(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch (e) {}
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
    }
    setIsListening(false);
  }, []);

  // Handle active state changes
  useEffect(() => {
    if (isActive) {
      startAnalysis();
    } else {
      stopAnalysis();
    }

    return () => stopAnalysis();
  }, [isActive, startAnalysis, stopAnalysis]);

  // Compact view for embedding
  if (compact) {
    const emotionData = EMOTION_DATA[emotion.dominant] || EMOTION_DATA.neutral;
    const sentimentPercent = ((sentiment + 1) / 2) * 100;

    return (
      <div style={styles.compactContainer}>
        <div style={styles.compactEmotion}>
          <span style={styles.compactEmoji}>{emotionData.emoji}</span>
          <span style={styles.compactLabel}>{emotionData.name}</span>
          <span style={styles.compactConfidence}>{Math.round(emotion.confidence * 100)}%</span>
        </div>
        <div style={styles.compactSentiment}>
          <div style={styles.sentimentBarCompact}>
            <div style={{
              ...styles.sentimentFillCompact,
              width: `${sentimentPercent}%`,
              background: sentiment > 0.2 ? '#4CAF50' : sentiment < -0.2 ? '#f44336' : '#FFC107'
            }} />
          </div>
        </div>
        {currentTip && (
          <div style={{
            ...styles.compactTip,
            borderColor: currentTip.type === 'urgent' ? '#f44336' :
                        currentTip.type === 'warning' ? '#FF9800' :
                        currentTip.type === 'success' ? '#4CAF50' : '#2196F3'
          }}>
            <span>{currentTip.icon}</span>
            <span style={styles.compactTipText}>{currentTip.title}</span>
          </div>
        )}
      </div>
    );
  }

  // Full view
  const emotionData = EMOTION_DATA[emotion.dominant] || EMOTION_DATA.neutral;
  const sentimentPercent = ((sentiment + 1) / 2) * 100;

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <span style={styles.headerIcon}>🎯</span>
        <span style={styles.headerTitle}>QSIS ניתוח רגשות</span>
        <span style={{
          ...styles.statusBadge,
          background: isListening ? '#4CAF50' : '#9e9e9e'
        }}>
          {isListening ? '🔴 מנתח' : '⚪ לא פעיל'}
        </span>
      </div>

      {/* Emotion Display */}
      <div style={styles.emotionSection}>
        <div style={{...styles.emotionCircle, borderColor: emotionData.color}}>
          <span style={styles.emotionEmoji}>{emotionData.emoji}</span>
        </div>
        <div style={styles.emotionInfo}>
          <div style={styles.emotionName}>{emotionData.name}</div>
          <div style={styles.emotionConfidence}>
            {Math.round(emotion.confidence * 100)}% ביטחון
          </div>
        </div>
      </div>

      {/* Sentiment Bar */}
      <div style={styles.sentimentSection}>
        <div style={styles.sentimentLabel}>סנטימנט</div>
        <div style={styles.sentimentBar}>
          <div style={{
            ...styles.sentimentFill,
            width: `${sentimentPercent}%`
          }} />
          <div style={{
            ...styles.sentimentMarker,
            left: `${sentimentPercent}%`
          }} />
        </div>
        <div style={styles.sentimentLabels}>
          <span>שלילי</span>
          <span>נייטרלי</span>
          <span>חיובי</span>
        </div>
      </div>

      {/* Stats */}
      <div style={styles.statsRow}>
        <div style={styles.statItem}>
          <div style={{...styles.statValue, color: '#4CAF50'}}>{stats.positive}</div>
          <div style={styles.statLabel}>חיובי</div>
        </div>
        <div style={styles.statItem}>
          <div style={{...styles.statValue, color: '#f44336'}}>{stats.negative}</div>
          <div style={styles.statLabel}>שלילי</div>
        </div>
        <div style={styles.statItem}>
          <div style={{...styles.statValue, color: '#FFC107'}}>{stats.neutral}</div>
          <div style={styles.statLabel}>נייטרלי</div>
        </div>
      </div>

      {/* Voice Metrics */}
      <div style={styles.metricsRow}>
        <div style={styles.metric}>
          <div style={styles.metricLabel}>גובה קול</div>
          <div style={styles.metricValue}>{Math.round(pitch)} Hz</div>
        </div>
        <div style={styles.metric}>
          <div style={styles.metricLabel}>עוצמה</div>
          <div style={styles.metricValue}>{Math.round(energy * 100)}%</div>
        </div>
        <div style={styles.metric}>
          <div style={styles.metricLabel}>סמנטי</div>
          <div style={styles.metricValue}>{sentiment.toFixed(2)}</div>
        </div>
      </div>

      {/* Coaching Tip */}
      {currentTip && (
        <div style={{
          ...styles.tipCard,
          borderColor: currentTip.type === 'urgent' ? '#f44336' :
                      currentTip.type === 'warning' ? '#FF9800' :
                      currentTip.type === 'success' ? '#4CAF50' : '#2196F3',
          background: currentTip.type === 'urgent' ? 'rgba(244, 67, 54, 0.1)' :
                     currentTip.type === 'warning' ? 'rgba(255, 152, 0, 0.1)' :
                     currentTip.type === 'success' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(33, 150, 243, 0.1)'
        }}>
          <div style={styles.tipIcon}>{currentTip.icon}</div>
          <div style={styles.tipContent}>
            <div style={styles.tipTitle}>{currentTip.title}</div>
            <div style={styles.tipText}>{currentTip.text}</div>
          </div>
        </div>
      )}

      {/* Last Transcript */}
      {transcript && (
        <div style={styles.transcriptBox}>
          <div style={styles.transcriptLabel}>תמלול אחרון:</div>
          <div style={styles.transcriptText}>"{transcript}"</div>
        </div>
      )}
    </div>
  );
};

// Styles
const styles = {
  container: {
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
    borderRadius: '16px',
    padding: '16px',
    color: '#fff',
    fontFamily: 'Heebo, Arial, sans-serif',
    direction: 'rtl'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '16px',
    paddingBottom: '12px',
    borderBottom: '1px solid rgba(255,255,255,0.1)'
  },
  headerIcon: {
    fontSize: '20px'
  },
  headerTitle: {
    fontSize: '16px',
    fontWeight: '600',
    flex: 1
  },
  statusBadge: {
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '500'
  },
  emotionSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '16px'
  },
  emotionCircle: {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    border: '3px solid',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(255,255,255,0.05)'
  },
  emotionEmoji: {
    fontSize: '32px'
  },
  emotionInfo: {
    flex: 1
  },
  emotionName: {
    fontSize: '20px',
    fontWeight: '600',
    marginBottom: '4px'
  },
  emotionConfidence: {
    fontSize: '14px',
    color: 'rgba(255,255,255,0.7)'
  },
  sentimentSection: {
    marginBottom: '16px'
  },
  sentimentLabel: {
    fontSize: '12px',
    color: 'rgba(255,255,255,0.7)',
    marginBottom: '8px'
  },
  sentimentBar: {
    height: '8px',
    background: 'linear-gradient(to right, #f44336, #FF9800, #FFC107, #8bc34a, #4CAF50)',
    borderRadius: '4px',
    position: 'relative'
  },
  sentimentFill: {
    height: '100%',
    borderRadius: '4px',
    transition: 'width 0.3s ease'
  },
  sentimentMarker: {
    position: 'absolute',
    top: '-4px',
    width: '16px',
    height: '16px',
    background: '#fff',
    borderRadius: '50%',
    transform: 'translateX(-50%)',
    boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
    transition: 'left 0.3s ease'
  },
  sentimentLabels: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '10px',
    color: 'rgba(255,255,255,0.5)',
    marginTop: '4px'
  },
  statsRow: {
    display: 'flex',
    justifyContent: 'space-around',
    padding: '12px',
    background: 'rgba(0,0,0,0.2)',
    borderRadius: '12px',
    marginBottom: '16px'
  },
  statItem: {
    textAlign: 'center'
  },
  statValue: {
    fontSize: '24px',
    fontWeight: '700'
  },
  statLabel: {
    fontSize: '11px',
    color: 'rgba(255,255,255,0.6)'
  },
  metricsRow: {
    display: 'flex',
    gap: '8px',
    marginBottom: '16px'
  },
  metric: {
    flex: 1,
    background: 'rgba(0,0,0,0.2)',
    padding: '10px',
    borderRadius: '8px',
    textAlign: 'center'
  },
  metricLabel: {
    fontSize: '10px',
    color: 'rgba(255,255,255,0.6)',
    marginBottom: '4px'
  },
  metricValue: {
    fontSize: '16px',
    fontWeight: '600'
  },
  tipCard: {
    display: 'flex',
    gap: '12px',
    padding: '12px',
    borderRadius: '12px',
    borderRight: '4px solid',
    marginBottom: '12px'
  },
  tipIcon: {
    fontSize: '24px'
  },
  tipContent: {
    flex: 1
  },
  tipTitle: {
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '4px'
  },
  tipText: {
    fontSize: '12px',
    color: 'rgba(255,255,255,0.8)',
    lineHeight: '1.4'
  },
  transcriptBox: {
    background: 'rgba(0,0,0,0.2)',
    padding: '10px',
    borderRadius: '8px'
  },
  transcriptLabel: {
    fontSize: '10px',
    color: 'rgba(255,255,255,0.5)',
    marginBottom: '4px'
  },
  transcriptText: {
    fontSize: '13px',
    fontStyle: 'italic'
  },
  // Compact styles
  compactContainer: {
    background: 'rgba(26, 26, 46, 0.95)',
    borderRadius: '12px',
    padding: '10px',
    color: '#fff',
    fontFamily: 'Heebo, Arial, sans-serif',
    direction: 'rtl'
  },
  compactEmotion: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px'
  },
  compactEmoji: {
    fontSize: '24px'
  },
  compactLabel: {
    fontSize: '14px',
    fontWeight: '600',
    flex: 1
  },
  compactConfidence: {
    fontSize: '12px',
    color: 'rgba(255,255,255,0.6)'
  },
  compactSentiment: {
    marginBottom: '8px'
  },
  sentimentBarCompact: {
    height: '4px',
    background: 'rgba(255,255,255,0.1)',
    borderRadius: '2px',
    overflow: 'hidden'
  },
  sentimentFillCompact: {
    height: '100%',
    borderRadius: '2px',
    transition: 'width 0.3s ease'
  },
  compactTip: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px',
    background: 'rgba(0,0,0,0.2)',
    borderRadius: '8px',
    borderRight: '3px solid',
    fontSize: '12px'
  },
  compactTipText: {
    flex: 1
  }
};

export default QSISEmotionAnalyzer;

// Export call type selector component
export const CallTypeSelector = ({ value, onChange }) => {
  return (
    <div style={{
      display: 'flex',
      gap: '8px',
      marginBottom: '12px'
    }}>
      <button
        onClick={() => onChange('service')}
        style={{
          flex: 1,
          padding: '10px',
          borderRadius: '8px',
          border: 'none',
          cursor: 'pointer',
          fontFamily: 'Heebo, Arial, sans-serif',
          fontWeight: '600',
          transition: 'all 0.2s',
          background: value === 'service' ? 'linear-gradient(135deg, #2196F3, #1976D2)' : 'rgba(255,255,255,0.1)',
          color: '#fff'
        }}
      >
        🎧 שירות
      </button>
      <button
        onClick={() => onChange('sales')}
        style={{
          flex: 1,
          padding: '10px',
          borderRadius: '8px',
          border: 'none',
          cursor: 'pointer',
          fontFamily: 'Heebo, Arial, sans-serif',
          fontWeight: '600',
          transition: 'all 0.2s',
          background: value === 'sales' ? 'linear-gradient(135deg, #9C27B0, #7B1FA2)' : 'rgba(255,255,255,0.1)',
          color: '#fff'
        }}
      >
        💼 מכירות
      </button>
    </div>
  );
};
